import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Icon, Input, Logo } from '@y365/design-system';
import type { SubmissionPayload, ValidationError } from '@y365/shared';
import * as api from './api/client.js';
import { SectionForm } from './components/SectionForm.js';

type Screen =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'code'; emailHint: string }
  | { kind: 'form'; session: api.SessionState }
  | { kind: 'done' };

/** Token comes from the path: /s/{token}. Never from a query string. */
function tokenFromPath(): string | null {
  const match = window.location.pathname.match(/^\/s\/([^/]+)/);
  return match?.[1] ?? null;
}

export function App() {
  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });
  const [token] = useState(tokenFromPath);

  const loadSession = useCallback(async () => {
    const session = await api.getSession();
    setScreen({ kind: 'form', session });
  }, []);

  useEffect(() => {
    if (!token) {
      setScreen({
        kind: 'invalid',
        message: 'This link is not complete. Please use the link from your email.',
      });
      return;
    }

    api
      .openInvite(token)
      .then((result) =>
        result.needsCode
          ? setScreen({ kind: 'code', emailHint: result.emailHint })
          : loadSession(),
      )
      .catch((error: unknown) =>
        setScreen({
          kind: 'invalid',
          message:
            error instanceof api.ApiError
              ? error.message
              : 'We could not open this link. It may have expired.',
        }),
      );
  }, [token, loadSession]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <a href="#main" className="y365-visually-hidden" data-y365-focusable>
        Skip to content
      </a>

      <header
        style={{
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Logo height={32} />
        </div>
      </header>

      <main id="main" style={{ maxWidth: 900, margin: '0 auto', padding: 'var(--sp-8) var(--sp-6)' }}>
        {screen.kind === 'loading' && <Centered>Loading…</Centered>}

        {screen.kind === 'invalid' && (
          <Notice tone="danger" icon="alert-circle" title="We can’t open this link">
            {screen.message}
            <p style={{ marginTop: 'var(--sp-3)' }}>
              If you think this is wrong, reply to the email we sent and we will send a new one.
            </p>
          </Notice>
        )}

        {screen.kind === 'code' && token && (
          <CodeScreen token={token} emailHint={screen.emailHint} onVerified={loadSession} />
        )}

        {screen.kind === 'form' && (
          <FormScreen session={screen.session} onAllDone={() => setScreen({ kind: 'done' })} />
        )}

        {screen.kind === 'done' && (
          <Notice tone="success" icon="check-circle" title="Thank you — that’s everything">
            We have what we need. If we have questions we will be in touch with the contact you gave us.
          </Notice>
        )}
      </main>
    </div>
  );
}

function FormScreen({
  session,
  onAllDone,
}: {
  session: api.SessionState;
  onAllDone: () => void;
}) {
  const [sections] = useState(session.sections);

  /*
    Tracked per visit, not from the stored status.

    A section already submitted on a previous visit stays editable: the customer
    holds a valid, unexpired link, and that link is the control. Filtering those
    out would show "Thank you, that's everything" with no way to correct
    anything — which is the opposite of letting people keep their details
    current.

    Expiry is what limits this. See docs/data-model.md.
  */
  const [doneThisVisit, setDoneThisVisit] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState(sections[0]?.sectionKey);

  /*
    Values for every section, not just the visible one.

    Switching tabs unmounts the form, and `session.sections[].values` is a
    snapshot from when the page loaded. Remounting from that snapshot would
    silently roll back anything typed since — so the live values live here, and
    the form reads from here when it comes back.
  */
  const [valuesByKey, setValuesByKey] = useState<Record<string, Record<string, unknown>>>(() =>
    Object.fromEntries(sections.map((s) => [s.sectionKey, s.values])),
  );

  const current = sections.find((s) => s.sectionKey === activeKey);

  const handleSubmit = useCallback(
    async (payload: SubmissionPayload): Promise<ValidationError[] | null> => {
      const result = await api.submitSection(payload.sectionKey, payload);
      if (!result.ok) return result.errors ?? [];

      setDoneThisVisit((prev) => {
        const next = new Set(prev).add(payload.sectionKey);

        // Move to the next section that still needs doing. Leaving them on a
        // just-submitted form makes them hunt for what is left; jumping to the
        // end when nothing is left would hide the confirmation.
        const remaining = sections.find((s) => !next.has(s.sectionKey));
        if (remaining) setActiveKey(remaining.sectionKey);

        return next;
      });

      return null;
    },
    [sections],
  );

  useEffect(() => {
    if (sections.length > 0 && doneThisVisit.size === sections.length) onAllDone();
  }, [sections, doneThisVisit, onAllDone]);

  /*
    Move focus to the tab when the section changes.

    Submitting auto-advances to the next section, which for a sighted user is
    obvious and for a screen reader user is not: focus would fall back to the
    body and they would be reading a different form with no idea it changed.

    Skipped on first render, where stealing focus from the top of the page would
    just be rude.
  */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (sections.length > 1 && activeKey) {
      document.getElementById(`tab-${activeKey}`)?.focus();
    }
  }, [activeKey, sections.length]);

  /*
    No sections at all.

    Reachable when every section on the invite was dropped server-side for
    having no definition — an agreements-only invite sent before that guard
    existed, for example. Without this the customer sits on "Finishing up…"
    forever, because the all-done effect below needs `sections.length > 0`.
  */
  if (sections.length === 0) {
    return (
      <Notice tone="danger" icon="alert-circle" title="There is nothing to fill in here yet">
        Your link works, but the part of the form it covers is not ready. This is our mistake, not
        yours — reply to the email we sent and we will sort it out.
      </Notice>
    );
  }

  if (!current) return <Centered>Finishing up…</Centered>;

  const section = current.definition.sections.find((s) => s.key === current.sectionKey);
  if (!section) {
    return (
      <Notice tone="danger" icon="alert-circle" title="Something is wrong with this form">
        We could not load this section. Please let us know and we will fix it.
      </Notice>
    );
  }

  return (
    <>
      {sections.length > 1 && (
        <SectionTabs
          sections={sections}
          activeKey={current.sectionKey}
          doneThisVisit={doneThisVisit}
          onSelect={setActiveKey}
        />
      )}

      <div
        role={sections.length > 1 ? 'tabpanel' : undefined}
        id={`panel-${current.sectionKey}`}
        aria-labelledby={sections.length > 1 ? `tab-${current.sectionKey}` : undefined}
      >
        <SectionForm
          key={current.sectionKey}
          definition={current.definition}
          section={section}
          previouslySubmittedOn={current.submittedOn}
          initialValues={valuesByKey[current.sectionKey] ?? current.values}
          recipientName={session.recipientName}
          recipientEmail={session.recipientEmail}
          onValuesChange={(values) =>
            setValuesByKey((prev) => ({ ...prev, [current.sectionKey]: values }))
          }
          onSaveDraft={async (values) => {
            await api.saveDraft(current.sectionKey, values);
          }}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}

/**
 * Section switcher.
 *
 * A real tablist rather than a row of buttons, so a keyboard user gets the
 * behaviour the shape promises: arrow keys move between tabs, Home and End jump
 * to the ends, and only the selected tab is a tab stop. A row of buttons looks
 * identical and forces them to Tab through every section to reach the form.
 */
function SectionTabs({
  sections,
  activeKey,
  doneThisVisit,
  onSelect,
}: {
  sections: api.SessionState['sections'];
  activeKey: string;
  doneThisVisit: Set<string>;
  onSelect: (key: api.SectionState['sectionKey']) => void;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (from: number, delta: number) => {
    const to = (from + delta + sections.length) % sections.length;
    const key = sections[to]!.sectionKey;
    onSelect(key);
    refs.current[key]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Parts of this form"
      style={{
        display: 'flex',
        gap: 'var(--sp-1)',
        marginBottom: 'var(--sp-5)',
        borderBottom: '1px solid var(--border-subtle)',
        flexWrap: 'wrap',
      }}
    >
      {sections.map((s, index) => {
        const active = s.sectionKey === activeKey;

        // Submitted earlier counts too — a returning customer should see what
        // they have already given us, not just what they did in this sitting.
        const complete = doneThisVisit.has(s.sectionKey) || s.status === 'submitted';

        return (
          <button
            key={s.sectionKey}
            ref={(el) => {
              refs.current[s.sectionKey] = el;
            }}
            role="tab"
            type="button"
            id={`tab-${s.sectionKey}`}
            aria-selected={active}
            aria-controls={`panel-${s.sectionKey}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(s.sectionKey)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(index, 1); }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); move(index, -1); }
              else if (e.key === 'Home') { e.preventDefault(); move(0, 0); }
              else if (e.key === 'End') { e.preventDefault(); move(sections.length - 1, 0); }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-3) var(--sp-4)',
              border: 0,
              borderBottom: `2px solid ${active ? 'var(--accent-primary)' : 'transparent'}`,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              fontWeight: active ? 600 : 400,
              color: active ? 'var(--text-brand)' : 'var(--text-body)',
              marginBottom: -1,
            }}
          >
            {/* Marked done by an icon AND by text for screen readers — colour
                and a tick alone say nothing to someone not looking. */}
            {complete && <Icon name="check-circle" size={16} aria-hidden />}
            {s.title}
            {complete && <span className="y365-visually-hidden"> — completed</span>}
          </button>
        );
      })}
    </div>
  );
}

function CodeScreen({
  token,
  emailHint,
  onVerified,
}: {
  token: string;
  emailHint: string;
  onVerified: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.verifyCode(token, code.trim());
      onVerified();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'That code did not work.');
      setBusy(false);
    }
  }

  return (
    <Card padding="lg" style={{ maxWidth: 480, margin: '0 auto' }}>
      <h1
        style={{
          margin: 0,
          marginBottom: 'var(--sp-3)',
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--fs-h3)',
          fontWeight: 'var(--fw-extrabold)',
          letterSpacing: '-0.02em',
        }}
      >
        Check your email
      </h1>
      <p style={{ marginBottom: 'var(--sp-6)', fontFamily: 'var(--font-body)', color: 'var(--text-body)' }}>
        We have sent a six-digit code to <strong>{emailHint}</strong>. Enter it below to open your form.
      </p>

      <form onSubmit={submit}>
        <Input
          label="Six-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          error={error}
          autoFocus
        />
        <div style={{ marginTop: 'var(--sp-5)' }}>
          <Button type="submit" block disabled={busy || code.trim().length < 6} data-y365-focusable>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
        </div>
      </form>

      <p
        style={{
          marginTop: 'var(--sp-5)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-muted)',
        }}
      >
        Not arrived? Check your junk folder — it can land there the first time.
      </p>
    </Card>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        textAlign: 'center',
        padding: 'var(--sp-10)',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </p>
  );
}

function Notice({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'danger' | 'success';
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  const color = tone === 'danger' ? 'var(--y-danger)' : 'var(--y-success)';
  return (
    <Card padding="lg" style={{ maxWidth: 560, margin: '0 auto' }}>
      <h1
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          margin: 0,
          marginBottom: 'var(--sp-3)',
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--fs-h3)',
          fontWeight: 'var(--fw-extrabold)',
          letterSpacing: '-0.02em',
          color,
        }}
      >
        <Icon name={icon} size={26} />
        {title}
      </h1>
      <div style={{ fontFamily: 'var(--font-body)', color: 'var(--text-body)' }}>{children}</div>
    </Card>
  );
}
